import { Controller } from "@hotwired/stimulus"

// Civic Warmth dialog controller — native <dialog>, no Bootstrap JS.
//
// Render inside a turbo_frame/stream and it opens on connect:
//   <dialog data-controller="dialog" class="...">
//     <button data-action="dialog#close">Cancel</button>
//   </dialog>
//
// Or wire a trigger elsewhere on the page (the controller element is NOT a
// dialog in this pattern — dialog-only listeners must not attach):
//   <button data-controller="dialog" data-action="dialog#open" data-dialog-id-param="send-edition">Send</button>
export default class extends Controller {
  connect() {
    if (!this.#isDialog) return

    if (!this.element.open) this.element.showModal()
    this.onClose = () => this.#teardown()
    // Close when clicking the backdrop (the dialog element itself, not its
    // children). Note: a padded <dialog> counts its padding as backdrop —
    // keep padding on an inner wrapper.
    this.onClick = (event) => {
      if (event.target === this.element) this.element.close()
    }
    this.element.addEventListener("close", this.onClose)
    this.element.addEventListener("click", this.onClick)
    // Never let Turbo snapshot an open dialog: restoring it from cache would
    // paint a stale, non-modal, backdrop-less dialog (open attribute survives
    // the snapshot but showModal() state does not).
    this.onBeforeCache = () => {
      this.element.close()
      if (this.element.dataset.dialogRemoveOnClose === "true") this.element.remove()
    }
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  disconnect() {
    if (!this.#isDialog) return

    this.element.removeEventListener("close", this.onClose)
    this.element.removeEventListener("click", this.onClick)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
  }

  open(event) {
    const id = event.params?.id
    const dialog = id ? document.getElementById(id) : this.element
    if (dialog?.tagName === "DIALOG") dialog.showModal()
  }

  close() {
    if (this.#isDialog) this.element.close()
  }

  get #isDialog() {
    return this.element.tagName === "DIALOG"
  }

  #teardown() {
    // Frame-rendered confirmation dialogs remove themselves after closing so
    // a reopened frame renders fresh state.
    if (this.element.dataset.dialogRemoveOnClose === "true") this.element.remove()
  }
}
